/**
 * The admin-triggered KYC notice.
 *
 * One mutation sends push, email and in-app notices from stored templates, in
 * the recipient's own account language. The dashboard submits only a user id
 * and a reason — no copy, language, channel list or scheduling.
 *
 * Sending a notice does not touch the user's KYC status. For `KYC_RESET` and
 * `KYC_SESSION_STUCK` the reset has to have been carried out first, since the
 * wording tells the user it already happened.
 */

/**
 * The thirteen reasons, in the order the handover lists them — grouped by what
 * went wrong, which is also how an admin looks for one.
 *
 * The enum value is what the service stores and matches its template on; the
 * label is display only and can be reworded freely.
 */
export const KYC_NOTIFICATION_REASONS = [
  {value: 'KYC_DOC_BLURRY', label: 'Blurry or unreadable ID photo'},
  {value: 'KYC_DOC_EXPIRED', label: 'Expired identity document'},
  {value: 'KYC_DOC_UNSUPPORTED', label: 'Unsupported document type'},
  {value: 'KYC_DOC_CROPPED', label: 'Document photo cropped'},
  {value: 'KYC_SELFIE_MISMATCH', label: 'Selfie does not match ID'},
  {value: 'KYC_SELFIE_QUALITY', label: 'Selfie too dark or blurry'},
  {value: 'KYC_INFO_MISMATCH', label: 'Personal details do not match ID'},
  {value: 'KYC_UNDERAGE', label: 'Minimum age requirement not met'},
  {value: 'KYC_DUPLICATE', label: 'Identity linked to another account'},
  {value: 'KYC_PROOF_ADDRESS_INVALID', label: 'Invalid proof of address'},
  {value: 'KYC_SANCTIONS_REVIEW', label: 'Additional compliance review'},
  {
    value: 'KYC_SESSION_STUCK',
    label: 'Verification interrupted; reset completed',
  },
  {value: 'KYC_RESET', label: 'Verification reset completed'},
  {value: 'KYC_OTHER', label: 'Other (custom reason)'},
];

/**
 * The one reason with no stored template.
 *
 * Everything else is a fixed notice the service composes itself. This one is
 * written by the agent, so it goes out through the ordinary
 * `sendNotificationToUsers` composer instead — the same modal the wallet,
 * order and swap tables use — where the wording can be drafted, translated
 * and reviewed before it is sent.
 */
export const CUSTOM_REASON = 'KYC_OTHER';

/**
 * The two reasons whose wording tells the user a reset already happened, so
 * the reset has to be done before the notice goes out.
 */
export const RESET_REASONS = ['KYC_RESET', 'KYC_SESSION_STUCK'];

/** The channels a dispatch reports on, in the order they are shown. */
export const CHANNELS = [
  {key: 'PUSH', label: 'Push'},
  {key: 'EMAIL', label: 'Email'},
  {key: 'IN_APP', label: 'In-app'},
];

/**
 * What each status means to an operator.
 *
 * `PUBLISHED` is deliberately not called "delivered": it means fanout accepted
 * it, not that a device or mailbox received it.
 */
export const CHANNEL_STATUS = {
  PUBLISHED: {label: 'Queued for delivery', tone: 'processing'},
  COMPLETE: {label: 'Added to inbox', tone: 'success'},
  SKIPPED: {label: 'Not sent', tone: 'warning'},
  PREPARED: {label: 'Dispatch not confirmed', tone: 'default'},
};

/** Why a channel was skipped, in words an operator can act on. */
export const SKIP_REASONS = {
  NO_PUSH_TOKEN: 'No push token registered for this account',
  NO_VALID_EMAIL: 'No valid email address on this account',
  PREFERENCE_DISABLED: 'The user turned this optional channel off',
};

/**
 * One row per channel, matched by `channel` rather than by position.
 *
 * The response array's order is not part of the contract, and a channel the
 * server did not report at all still needs a row — otherwise a missing entry
 * would read as a silent success.
 */
export const readChannelResults = (channels = []) =>
  CHANNELS.map(({key, label}) => {
    const result = (channels || []).find(entry => entry?.channel === key);
    const status = result?.status;
    const meta = CHANNEL_STATUS[status];

    return {
      key,
      label,
      status,
      statusLabel: meta?.label ?? (status ? status : 'No result reported'),
      tone: meta?.tone ?? 'default',
      // Only a skip carries an explanation worth showing.
      detail:
        status === 'SKIPPED'
          ? SKIP_REASONS[result?.reason] ?? result?.reason ?? undefined
          : undefined,
      notificationId: result?.notificationId,
    };
  });

/** The label for a reason, falling back to the raw enum if it is unknown. */
export const reasonLabel = value =>
  KYC_NOTIFICATION_REASONS.find(reason => reason.value === value)?.label ??
  value;
